/**
 * Enum for module types used in polymorphic relationships
 * This exactly matches the ModuleType enum in the Prisma schema
 */
export enum ModuleTypeEnum {
  PROJECT = 'PROJECT',
  TASK = 'TASK',
  TICKET = 'TICKET',
  TASK_GROUP = 'TASK_GROUP',
  DEAL = 'DEAL',
  EVENT = 'EVENT',
  ENTITY = 'ENTITY',
  CONTRACT = 'CONTRACT',
  INVOICE = 'INVOICE',
  EXPENSE = 'EXPENSE',
  THREAD = 'THREAD',
  THREAD_COMMENT = 'THREAD_COMMENT',
  TIME_ENTRY = 'TIME_ENTRY',
  TEAM = 'TEAM',
  INVENTORY = 'INVENTORY',
  // Sub-tables — see SUB_TABLE_PARENT_MODULE below. Not Prisma ModuleType
  // members: never write these to an enum-typed column.
  INVENTORY_MOVEMENT = 'INVENTORY_MOVEMENT',
  INVENTORY_LOCATION = 'INVENTORY_LOCATION',
  INVENTORY_CATEGORY = 'INVENTORY_CATEGORY',
  SUPPLIER = 'SUPPLIER',
  MEMBER = 'MEMBER',
  DOCUMENT = 'DOCUMENT',
  DOCUMENT_CATEGORY = 'DOCUMENT_CATEGORY',
  ORGANIZATION = 'ORGANIZATION',
  TODAY = 'TODAY',
  CUSTOM_FIELD = 'CUSTOM_FIELD',
  CUSTOM_COLLECTION = 'CUSTOM_COLLECTION',
  PERMISSION = 'PERMISSION',
  PERMISSION_SET = 'PERMISSION_SET',
  FEATURE_FLAG = 'FEATURE_FLAG',
  INTEGRATION = 'INTEGRATION',
  AI = 'AI',
  NOTE = 'NOTE',
  CONTACT = 'CONTACT',
  PROFILE = 'PROFILE',
  CALENDAR = 'CALENDAR',
  CYCLE_INSTANCE = 'CYCLE_INSTANCE',
  CYCLE_TEMPLATE = 'CYCLE_TEMPLATE',
  FORM = 'FORM',
  FORM_SUBMISSION = 'FORM_SUBMISSION',
  WORKFLOW = 'WORKFLOW',
  ANALYTICS = 'ANALYTICS',
  BILLING_AUTHORIZATION = 'BILLING_AUTHORIZATION',
  WORKSTREAM = 'WORKSTREAM',
  DEPLOYMENT = 'DEPLOYMENT',
  QUOTE = 'QUOTE',
  KNOWLEDGE_BASE = 'KNOWLEDGE_BASE',
  KNOWLEDGE_BASE_CATEGORY = 'KNOWLEDGE_BASE_CATEGORY',
  MARKETING = 'MARKETING',
  // Profitability module (org P&L, forecast, scenarios, rates, earnings).
  // Display name is "Profitability"; internal name stays FINANCIALS.
  FINANCIALS = 'FINANCIALS',
  // Backing notes for note templates (hidden from note lists/search)
  NOTE_TEMPLATE = 'NOTE_TEMPLATE',
}

// Type guard to check if a string is a valid ModuleType
export function isValidModuleType(value: string): value is ModuleTypeEnum {
  return Object.values(ModuleTypeEnum).includes(value as ModuleTypeEnum)
}

/**
 * Tables that live inside another module rather than being modules of their
 * own. The Inventory tabs — Products, Movements, Locations, Categories — are
 * four different tables with different columns, and everything scoped "per
 * module" (saved views, the active-view pointer, column preferences, table
 * preferences) is keyed by module type. One type for all four meant one view
 * list and one column layout shared across tables whose columns collide, so
 * hiding a column on one tab hid it on another.
 *
 * These are deliberately NOT members of the Prisma `ModuleType` enum. They
 * only ever reach string-typed storage — `saved_queries.modules`,
 * `column_preferences.module_type`, `user_preferences.module_type` — so no
 * migration is needed to introduce one. The rule that keeps it that way:
 * **never let a sub-table module type reach an enum-typed column** (custom
 * fields, permissions, audit, workflows, time-entry links, module
 * references). Fold to the parent with `getPermissionModuleType`, or skip the
 * feature for sub-tables the way BaseTable skips custom fields. Adding a new
 * one to a picker that writes an enum column means adding the enum value
 * first.
 */
export const SUB_TABLE_PARENT_MODULE: Partial<
  Record<ModuleTypeEnum, ModuleTypeEnum>
> = {
  [ModuleTypeEnum.INVENTORY_MOVEMENT]: ModuleTypeEnum.INVENTORY,
  [ModuleTypeEnum.INVENTORY_LOCATION]: ModuleTypeEnum.INVENTORY,
  [ModuleTypeEnum.INVENTORY_CATEGORY]: ModuleTypeEnum.INVENTORY,
}

/** True for a table that is scoped by module type but is not its own module. */
export function isSubTableModuleType(moduleType: ModuleTypeEnum): boolean {
  return moduleType in SUB_TABLE_PARENT_MODULE
}

/**
 * The module type a permission check should be made against. Sub-tables fold
 * onto their parent module — they are granted and revoked with it, and have no
 * permission definitions of their own. Everything else answers for itself.
 */
export function getPermissionModuleType(
  moduleType: ModuleTypeEnum,
): ModuleTypeEnum {
  return SUB_TABLE_PARENT_MODULE[moduleType] ?? moduleType
}
